/**
 * MitigationCard Component
 *
 * Displays a single mitigation with code, name, level, commentary, evidence, and citations.
 * Used by GroundRiskMitigations and AirRiskMitigations components to display SORA mitigations.
 * Supports visual distinction for user-corrected values.
 *
 * Requirements: 5.3
 */

import React from 'react';
import { CitationLink } from '../CitationLink';
import { FeedbackButton } from '../FeedbackButton';
import {
  Citation,
  ConfidenceLevel,
  MitigationLevel,
  GroundRiskMitigation,
  AirRiskMitigation,
} from '../../types/extraction';
import { ConfidenceBadge } from '../ConfidenceBadge';
import { Feedback } from '../../types/feedback';
import styles from './MitigationCard.module.css';

/**
 * Generic mitigation interface that works for both ground and air risk mitigations
 */
export interface Mitigation {
  /** Unique identifier for the mitigation */
  id: string;
  /** Mitigation code (e.g., 'M1A', 'SM1') */
  code: string;
  /** Display name of the mitigation */
  name: string;
  /** Selected level (for ground risk) or boolean value (for air risk) */
  selectedLevel: MitigationLevel | boolean;
  /** Confidence level of the extraction */
  confidence?: ConfidenceLevel;
  /** LLM-generated commentary explaining the assessment */
  commentary: string;
  /** Supporting evidence text */
  evidence: string;
  /** Citations supporting this mitigation */
  citations: Citation[];
  /** User-corrected level if feedback was provided (optional) */
  userCorrectedLevel?: MitigationLevel | boolean;
  /** Feedback record if provided (optional) */
  feedback?: Feedback;
}

/**
 * Props for the MitigationCard component
 */
export interface MitigationCardProps {
  /** The mitigation to display */
  mitigation: Mitigation;
  /** Callback when a citation is clicked */
  onCitationClick: (citation: Citation) => void;
  /** Callback when the feedback button is clicked */
  onFeedbackClick: (mitigationId: string) => void;
}

/**
 * Maps mitigation levels to display labels
 */
const LEVEL_LABELS: Record<MitigationLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  not_applicable: 'N/A',
};

/**
 * Available options per ground risk mitigation code
 */
const GROUND_MITIGATION_OPTIONS: Record<string, MitigationLevel[]> = {
  M1A: ['not_applicable', 'low', 'medium'],
  M1B: ['not_applicable', 'medium', 'high'],
  M1C: ['not_applicable', 'low'],
  M2: ['not_applicable', 'medium', 'high'],
};

/**
 * Available options for air risk mitigations (SM1-SM10)
 */
const AIR_MITIGATION_OPTIONS: boolean[] = [false, true];

/**
 * Formats the selected level for display
 */
const formatLevel = (level: MitigationLevel | boolean): string => {
  if (typeof level === 'boolean') {
    return level ? 'Yes' : 'No';
  }
  return LEVEL_LABELS[level] || String(level);
};

/**
 * Determines if the mitigation is a ground risk mitigation (has MitigationLevel)
 */
const isGroundRiskMitigation = (
  level: MitigationLevel | boolean
): level is MitigationLevel => {
  return typeof level !== 'boolean';
};

/**
 * Converts a GroundRiskMitigation to the generic Mitigation interface
 */
export const fromGroundRiskMitigation = (
  mitigation: GroundRiskMitigation,
  feedback?: Feedback
): Mitigation => ({
  id: mitigation.id,
  code: mitigation.code,
  name: mitigation.name,
  selectedLevel: mitigation.selectedLevel,
  confidence: mitigation.confidence,
  commentary: mitigation.commentary,
  evidence: mitigation.evidence,
  citations: mitigation.citations,
  userCorrectedLevel: mitigation.userCorrectedLevel,
  feedback,
});

/**
 * Converts an AirRiskMitigation to the generic Mitigation interface
 */
export const fromAirRiskMitigation = (
  mitigation: AirRiskMitigation,
  feedback?: Feedback
): Mitigation => ({
  id: mitigation.id,
  code: mitigation.code,
  name: mitigation.name,
  selectedLevel: mitigation.selectedValue,
  confidence: mitigation.confidence,
  commentary: mitigation.commentary,
  evidence: mitigation.evidence,
  citations: mitigation.citations,
  userCorrectedLevel: mitigation.userCorrectedValue,
  feedback,
});

/**
 * MitigationCard displays a single mitigation with:
 * - Mitigation code prominently displayed (e.g., M1A, SM1)
 * - Mitigation name
 * - Selected level with appropriate styling
 * - LLM-generated commentary explaining the assessment
 * - Supporting evidence text
 * - Clickable citations
 * - Feedback button
 * - Visual distinction for user-corrected values
 *
 * @example
 * <MitigationCard
 *   mitigation={groundRiskMitigation}
 *   onCitationClick={handleCitationClick}
 *   onFeedbackClick={handleFeedbackClick}
 * />
 */
export const MitigationCard: React.FC<MitigationCardProps> = ({
  mitigation,
  onCitationClick,
  onFeedbackClick,
}) => {
  const hasCorrectedLevel = mitigation.userCorrectedLevel !== undefined;
  const hasFeedback = mitigation.feedback !== undefined;
  const feedbackIsCorrect = mitigation.feedback?.isCorrect ?? false;

  // Determine the display level (corrected or selected)
  const displayLevel = hasCorrectedLevel
    ? mitigation.userCorrectedLevel!
    : mitigation.selectedLevel;

  const isGroundRisk = isGroundRiskMitigation(mitigation.selectedLevel);

  return (
    <article
      className={`${styles.card} ${hasCorrectedLevel ? styles.correctedCard : ''}`}
      data-mitigation-id={mitigation.id}
      aria-labelledby={`mitigation-title-${mitigation.id}`}
    >
      {/* Header Section */}
      <header className={styles.header}>
        <div className={styles.titleSection}>
          <span className={styles.code}>{mitigation.code}</span>
          <h3
            id={`mitigation-title-${mitigation.id}`}
            className={styles.name}
          >
            {mitigation.name}
          </h3>
        </div>
        <div className={styles.headerActions}>
          <FeedbackButton
            itemId={mitigation.id}
            onClick={onFeedbackClick}
            hasFeedback={hasFeedback}
            feedbackIsCorrect={feedbackIsCorrect}
            size="medium"
          />
        </div>
      </header>

      {/* Level Options Section */}
      <div className={styles.levelSection}>
        <div className={styles.levelLeft}>
          <div className={styles.optionsRow}>
            {isGroundRisk ? (
              (GROUND_MITIGATION_OPTIONS[mitigation.code] || []).map((option) => (
                <span
                  key={option}
                  className={`${styles.optionPill} ${displayLevel === option ? styles.optionSelected : ''}`}
                  role="status"
                  aria-label={`${formatLevel(option)}${displayLevel === option ? ' (selected)' : ''}`}
                >
                  {formatLevel(option)}
                </span>
              ))
            ) : (
              AIR_MITIGATION_OPTIONS.map((option) => (
                <span
                  key={String(option)}
                  className={`${styles.optionPill} ${displayLevel === option ? styles.optionSelected : ''}`}
                  role="status"
                  aria-label={`${formatLevel(option)}${displayLevel === option ? ' (selected)' : ''}`}
                >
                  {formatLevel(option)}
                </span>
              ))
            )}
          </div>
          {hasCorrectedLevel && (
            <div className={styles.originalLevel}>
              <span className={styles.originalLabel}>Original:</span>
              <span className={styles.originalValue}>
                {formatLevel(mitigation.selectedLevel)}
              </span>
            </div>
          )}
        </div>
        {mitigation.confidence && (
          <div className={styles.confidenceRight}>
            <span className={styles.confidenceLabel}>Confidence:</span>
            <ConfidenceBadge level={mitigation.confidence} showLabel />
          </div>
        )}
      </div>

      {/* Commentary Section */}
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Commentary</h4>
        <p className={styles.commentary}>
          {mitigation.commentary || 'No commentary available.'}
        </p>
      </section>

      {/* Evidence Section */}
      {/* <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Evidence</h4>
        <p className={styles.evidence}>
          {mitigation.evidence || 'No evidence available.'}
        </p>
      </section> */}

      {/* Citations Section */}
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>Citations</h4>
        <div className={styles.citationsContainer}>
          {mitigation.citations.length > 0 ? (
            mitigation.citations.map((citation, index) => (
              <CitationLink
                key={citation.id}
                citation={citation}
                onClick={onCitationClick}
                index={index + 1}
                compact={false}
              />
            ))
          ) : (
            <span className={styles.noCitations}>No citations available</span>
          )}
        </div>
      </section>
    </article>
  );
};

export default MitigationCard;
